
import { sanitizeInput } from './inputValidation';

interface ContentValidationResult {
  isValid: boolean;
  errors: string[];
  sanitizedContent?: string;
}

export const validateContentSafety = (content: string): ContentValidationResult => {
  const errors: string[] = [];
  
  // Basic length validation
  if (content.length > 10000) {
    errors.push('Content exceeds maximum length of 10,000 characters');
  }

  if (content.length < 1) {
    errors.push('Content cannot be empty');
  }

  // Sanitize the content
  const sanitizedContent = sanitizeInput(content);
  
  // Check for suspicious patterns
  const suspiciousPatterns = [
    /script\s*:/i,
    /javascript\s*:/i,
    /data\s*:/i,
    /vbscript\s*:/i,
    /on\w+\s*=/i,
    /<script/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /eval\s*\(/i,
    /exec\s*\(/i,
    /system\s*\(/i,
    /cmd\s*\(/i
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(sanitizedContent)) {
      errors.push('Content contains potentially unsafe patterns');
      break;
    }
  }

  // Check for explicit content markers (basic)
  const explicitPatterns = [
    /\b(explicit|adult|nsfw|sexual)\b/i
  ];

  const hasExplicitContent = explicitPatterns.some(pattern => 
    pattern.test(sanitizedContent)
  );

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedContent: errors.length === 0 ? sanitizedContent : undefined
  };
};

export const validateMeditationContent = (content: string, category: string): ContentValidationResult => {
  const baseValidation = validateContentSafety(content);
  
  if (!baseValidation.isValid) {
    return baseValidation;
  }

  // Additional validation for meditation content
  const errors: string[] = [];

  // Check for meditation-appropriate content
  if (category === 'explicit_meditation') {
    // Allow more flexible content for explicit meditation
    const inappropriatePatterns = [
      /\b(violence|harm|death|kill)\b/i,
      /\b(hate|anger|revenge)\b/i
    ];

    for (const pattern of inappropriatePatterns) {
      if (pattern.test(content)) {
        errors.push('Content contains inappropriate themes for meditation');
        break;
      }
    }
  } else {
    // Stricter validation for general meditation content
    const inappropriatePatterns = [
      /\b(violence|harm|death|kill|sexual|explicit)\b/i,
      /\b(hate|anger|revenge|aggressive)\b/i
    ];

    for (const pattern of inappropriatePatterns) {
      if (pattern.test(content)) {
        errors.push('Content contains themes inappropriate for this meditation category');
        break;
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors: [...baseValidation.errors, ...errors],
    sanitizedContent: errors.length === 0 ? baseValidation.sanitizedContent : undefined
  };
};
